'use client';

import { useTranslations } from 'next-intl';
import { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import Navbar from '@/components/landing/navbar/Navbar';
import Footer from '@/components/landing/footer/FooterV3';

interface LegalPageLayoutProps {
  titleKey: string;
  subtitleKey?: string;
  /** @deprecated kept for backwards-compat with existing pages; ignored */
  icon?: LucideIcon;
  /** @deprecated kept for backwards-compat with existing pages; ignored */
  iconColor?: string;
  children: ReactNode;
  /** Eyebrow text, e.g. "RECHTLICHES" */
  eyebrow?: string;
}

/**
 * LegalPageLayout v2 — matches landing design system.
 *
 * - Subtle mesh-glow background like Hero (not gradient-banner)
 * - Eyebrow-with-dot + Display-Lg headline
 * - Single content card with subtle border + heavy shadow
 * - FooterV3 (light minimal)
 */
export default function LegalPageLayout({
  titleKey,
  subtitleKey,
  children,
  eyebrow = 'RECHTLICHES',
}: LegalPageLayoutProps) {
  const t = useTranslations();

  return (
    <div className="relative min-h-screen overflow-hidden bg-white">
      <Navbar />

      {/* Background — subtle mesh glow + dot grid */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[600px]"
        aria-hidden="true"
      >
        <div className="absolute left-1/2 top-0 h-[500px] w-[900px] -translate-x-1/2 -translate-y-1/3 rounded-full bg-blue-100/40 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.3]"
          style={{
            backgroundImage:
              'radial-gradient(circle, rgba(15,23,42,0.08) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage:
              'radial-gradient(ellipse 60% 50% at 50% 20%, black 30%, transparent 80%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 60% 50% at 50% 20%, black 30%, transparent 80%)',
          }}
        />
      </div>

      <main className="relative mx-auto max-w-4xl px-4 pt-28 pb-20 sm:px-6 sm:pt-36 sm:pb-24 lg:px-8">
        {/* Header */}
        <div className="mb-10 sm:mb-14">
          <p className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-blue-600">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full bg-blue-600"
              aria-hidden="true"
            />
            {eyebrow}
          </p>
          <h1 className="mt-4 text-[clamp(2rem,4.4vw,3.75rem)] font-semibold leading-[1.08] tracking-[-0.03em] text-gray-950">
            {t(titleKey)}
          </h1>
          {subtitleKey && (
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-gray-600 sm:text-lg">
              {t(subtitleKey)}
            </p>
          )}
        </div>

        {/* Content card */}
        <article className="relative overflow-hidden rounded-3xl border border-gray-200/80 bg-white p-7 sm:p-10 lg:p-12 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_24px_56px_-16px_rgba(15,23,42,0.10)]">
          <div className="prose prose-gray max-w-none prose-headings:font-semibold prose-headings:tracking-[-0.015em] prose-headings:text-gray-950 prose-a:text-blue-600 prose-a:font-medium prose-a:no-underline hover:prose-a:underline">
            {children}
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
